import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  Button,
  DialogActions,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  LinearProgress,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import SaveIcon from '@mui/icons-material/Save';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DoneIcon from '@mui/icons-material/Done';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { neon } from '@neondatabase/serverless';

// Get the database URL from environment variables or use direct string for development
const getDatabaseUrl = () => {
  return 'postgres://neondb_owner:npg_Ub9EiNceZ3ro@ep-divine-sun-a18caqng-pooler.ap-southeast-1.aws.neon.tech/neondb';
};

const AnalysisDialog = ({ open, onClose, data, nodes, practice_time, conversationId }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const analysisData = typeof data === 'string' ? JSON.parse(data) : data;
  const navigate = useNavigate();
  
  // Database related states
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [databaseUrl] = useState(() => getDatabaseUrl());
  const [memberId, setMemberId] = useState(null);
  const [hasAutoSaved, setHasAutoSaved] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Get all nodes from the ice_breaking_phase
  // First try to use the nodes prop if available, otherwise try to find it from analysisData
  const iceBreakingNodes = nodes && nodes.length > 0 
    ? nodes.find(n => n.id === 'ice_breaking_phase')?.nodes || []
    : analysisData?.nodes?.find(n => n.id === 'ice_breaking_phase')?.nodes || [];

  // console.log('Using nodes data:', iceBreakingNodes.length > 0 ? 'From props' : 'From analysisData');

  // Set the first node as selected by default when component mounts or nodes change
  useEffect(() => {
    if (iceBreakingNodes && iceBreakingNodes.length > 0 && open) {
      setSelectedNode(iceBreakingNodes[0]);
    }
  }, [iceBreakingNodes, open]);

  // Auto-save data to database when dialog is opened
  useEffect(() => {
    // Only auto-save when dialog is opened and has not already been saved in this session
    if (open && !hasAutoSaved && analysisData && conversationId) {
      // Add a small delay to ensure all data is properly loaded
      const saveTimeout = setTimeout(() => {
        console.log('Auto-saving analysis data to database...');
        saveAnalysisResults(true).then(() => {
          setHasAutoSaved(true);  // Mark as saved to prevent duplicate saves
        });
      }, 1000); // 1 second delay
      
      return () => clearTimeout(saveTimeout); // Clean up timeout on unmount
    }
    
    // Reset the auto-save flag when dialog is closed
    if (!open) {
      setHasAutoSaved(false);
    }
  }, [open, analysisData, conversationId, hasAutoSaved]);

  // Get member_id from localStorage and initialize database when component mounts
  useEffect(() => {
    const getMemberId = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          if (user && user.id) {
            setMemberId(user.id);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    };

    const setupDatabase = async () => {
      await initDatabase();
      await ensureColumns();
    };
    
    getMemberId();
    setupDatabase();
  }, []);

  // Function to initialize the database table
  const initDatabase = async () => {
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Test connection
      const testResult = await sql`SELECT 1 as test`;
      console.log('Connection test result:', testResult);
      setConnectionStatus('Connected to database');
      
      // Create the analysis_results table if it doesn't exist
      await sql`
        CREATE TABLE IF NOT EXISTS analysis_results (
          id SERIAL PRIMARY KEY,
          member_id BIGINT,
          conversation_id BIGINT,
          analysis_data JSON,
          total_score INTEGER,
          completed_checkpoints INTEGER,
          incomplete_checkpoints INTEGER,
          completion_rate INTEGER,
          practice_time INTEGER,
          nodes_data JSON,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT (CURRENT_TIMESTAMP AT TIME ZONE 'Asia/Taipei')
        )
      `;
      
    } catch (err) {
      console.error('Error initializing database:', err);
      setConnectionStatus(`Database connection failed: ${err.message}`);
    }
  };

  // Function to ensure all necessary columns exist
  const ensureColumns = async () => {
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Check if columns exist and add if missing
      // Check if practice_time column exists
      const ptColumnCheck = await sql`
        SELECT column_name 
        FROM information_schema.columns 
        WHERE table_name = 'analysis_results' 
        AND column_name = 'practice_time';
      `;
      
      if (ptColumnCheck.length === 0) {
        console.log('Adding practice_time column to analysis_results table');
        await sql`
          ALTER TABLE analysis_results 
          ADD COLUMN practice_time INTEGER;
        `;
      }
      
      // Check if nodes_data column exists
      const nodesColumnCheck = await sql`
        SELECT column_name 
        FROM information_schema.columns 
        WHERE table_name = 'analysis_results' 
        AND column_name = 'nodes_data';
      `;
      
      if (nodesColumnCheck.length === 0) {
        console.log('Adding nodes_data column to analysis_results table');
        await sql`
          ALTER TABLE analysis_results 
          ADD COLUMN nodes_data JSON;
        `;
      }
      
    } catch (err) {
      console.error('Error checking/adding columns:', err);
    }
  };

  // Function to save analysis results to the database
  const saveAnalysisResults = async (isSilent = false) => {
    setIsLoading(true);
    
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      if (!analysisData) {
        throw new Error('No analysis data available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Calculate values
      const { completed, incomplete } = calculateCheckpoints();
      const totalScore = Math.round(analysisData.scores.total);
      
      // Ensure completion_rate is an integer
      console.log('Original completion_rate:', analysisData?.completion_rate, typeof analysisData?.completion_rate);
      const completionRate = Math.round(Number(analysisData?.completion_rate) || 0);
      console.log('Converted completion_rate:', completionRate, typeof completionRate);
      
      // Ensure practice_time is an integer
      console.log('Original practice_time:', practice_time, typeof practice_time);
      const practiceTimeInt = Math.round(Number(practice_time) || 0);
      console.log('Converted practice_time:', practiceTimeInt, typeof practiceTimeInt);
      
      // Format nodes data for database
      console.log('Saving nodes data:', nodes);
      const nodesData = nodes ? JSON.stringify(nodes) : null;
      
      // Check if record already exists for this conversation
      const existingRecord = await sql`
        SELECT id FROM analysis_results
        WHERE conversation_id = ${conversationId ? parseInt(conversationId) : null}
      `;
      
      if (existingRecord.length > 0) {
        // Update existing record
        await sql`
          UPDATE analysis_results SET
            analysis_data = ${JSON.stringify(analysisData)},
            total_score = ${totalScore},
            completed_checkpoints = ${completed},
            incomplete_checkpoints = ${incomplete},
            completion_rate = ${completionRate},
            practice_time = ${practiceTimeInt},
            nodes_data = ${nodesData},
            created_at = (CURRENT_TIMESTAMP AT TIME ZONE 'Asia/Taipei')
          WHERE conversation_id = ${conversationId ? parseInt(conversationId) : null}
        `;
        
        if (!isSilent) {
          setSnackbar({
            open: true,
            message: '評估報告已更新',
            severity: 'success'
          });
        }
      } else {
        // Insert new record
        await sql`
          INSERT INTO analysis_results (
            member_id,
            conversation_id,
            analysis_data,
            total_score,
            completed_checkpoints,
            incomplete_checkpoints,
            completion_rate,
            practice_time,
            nodes_data,
            created_at
          ) VALUES (
            ${memberId ? parseInt(memberId) : null},
            ${conversationId ? parseInt(conversationId) : null},
            ${JSON.stringify(analysisData)},
            ${totalScore},
            ${completed},
            ${incomplete},
            ${completionRate},
            ${practiceTimeInt},
            ${nodesData},
            (CURRENT_TIMESTAMP AT TIME ZONE 'Asia/Taipei')
          )
        `;
        
        if (!isSilent) {
          setSnackbar({
            open: true,
            message: '評估報告已儲存',
            severity: 'success'
          });
        }
      }
      
    } catch (err) {
      console.error('Error saving analysis results:', err);
      if (!isSilent) {
        setSnackbar({
          open: true,
          message: `儲存失敗: ${err.message}`,
          severity: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle closing the snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  if (!analysisData) {
    return null;
  }

  // Function to render checkpoint status
  const renderCheckpointStatus = (checkpoint) => {
    // Ensure the checkpoint object has the required properties
    const isCompleted = checkpoint.completed !== undefined 
      ? checkpoint.completed 
      : checkpoint.status === 'completed';
    
    const checkpointText = checkpoint.text || checkpoint.description || '';
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {isCompleted ? (
          <CheckCircleIcon sx={{ color: '#4CAF50' }} />
        ) : (
          <CancelIcon sx={{ color: '#FF6B6B' }} />
        )}
        <Typography variant="body2">
          {checkpointText}
        </Typography>
      </Box>
    );
  };

  // Calculate completed and incomplete checkpoints
  const calculateCheckpoints = () => {
    let completed = 0;
    let incomplete = 0;

    if (iceBreakingNodes && iceBreakingNodes.length > 0) {
      iceBreakingNodes.forEach(node => {
        if (node.checkpoint_status) {
          node.checkpoint_status.forEach(status => {
            // Handle different data structures
            const isCompleted = status.completed !== undefined 
              ? status.completed 
              : status.status === 'completed';
            
            if (isCompleted) {
              completed++;
            } else {
              incomplete++;
            }
          });
        } else if (node.checkpoints) {
          // Alternative data structure
          node.checkpoints.forEach(checkpoint => {
            const isCompleted = checkpoint.completed !== undefined 
              ? checkpoint.completed 
              : checkpoint.status === 'completed';
            
            if (isCompleted) {
              completed++;
            } else {
              incomplete++;
            }
          });
        }
      });
    }

    return { completed, incomplete };
  };

  const { completed, incomplete } = calculateCheckpoints();

  // Function to handle clicking "完成評估" button
  const handleEvaluation = () => {
    // Save the analysis results before navigating
    saveAnalysisResults().then(() => {
      // Navigate to POCEvaluationForm with the conversation ID
      navigate(`/chat-evaluation/${conversationId}`);
      onClose();
    });
  };

  // Function to handle clicking "儲存結果" button
  const handleSaveResults = () => {
    // If already auto-saved, just show a notification without reloading
    if (hasAutoSaved) {
      setSnackbar({
        open: true,
        message: '評估報告已儲存',
        severity: 'info'
      });
      return;
    }
    
    // Otherwise, do a full save
    saveAnalysisResults();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: '#ffffff',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        background: 'linear-gradient(90deg, #f8fafc 0%, #e0f2fe 100%)'
      }}>
        <Typography variant="h5" fontWeight="700" color="#0f172a">
          評估報告
        </Typography>
        {connectionStatus && (
          <Typography variant="caption" color={connectionStatus.includes('failed') ? 'error' : 'success.main'}>
            {connectionStatus}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
        <Box sx={{ mb: 4 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 3, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h3" fontWeight="bold" color="white" sx={{ mb: 0.5 }}>
                  {Math.round(analysisData.scores.total)}
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.9)">
                  總體表現
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>
                    {completed}
                  </Box>
                  <Typography variant="subtitle2" color="rgba(255,255,255,0.9)">
                    達成查核點
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>
                    {incomplete}
                  </Box>
                  <Typography variant="subtitle2" color="rgba(255,255,255,0.9)">
                    未達成查核點
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="rgba(255,255,255,0.9)" sx={{ mb: 0.5 }}>
                對話完成度: {analysisData?.completion_rate || '0'}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={Number(analysisData?.completion_rate) || 0} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'white'
                  }
                }}
              />
            </Box>
          </Paper>
            
          <Paper elevation={1} sx={{ p: 2.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <AccessTimeIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="600">
              本次練習時間：{Math.floor(practice_time / 60).toString().padStart(2, '0')}:{(practice_time % 60).toString().padStart(2, '0')}
            </Typography>
          </Paper>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              節點評估
            </Typography>
          </Box>
          
          <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
            {/* Node selection chips */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              {iceBreakingNodes.map((node) => (
                <Chip
                  key={node.id}
                  label={node.text}
                  onClick={() => setSelectedNode(node)}
                  variant={selectedNode?.id === node.id ? "filled" : "outlined"}
                  color={selectedNode?.id === node.id ? "primary" : "default"}
                  sx={{ 
                    borderRadius: '100px',
                    py: 0.5,
                    fontWeight: selectedNode?.id === node.id ? 600 : 400,
                    boxShadow: selectedNode?.id === node.id ? '0 2px 8px rgba(37, 99, 235, 0.2)' : 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)'
                    }
                  }}
                />
              ))}
            </Box>

            {/* Selected node details */}
            {selectedNode && (
              <Box sx={{ 
                bgcolor: '#f0f9ff',
                borderRadius: 3,
                p: 2.5,
                borderLeft: '4px solid',
                borderColor: 'primary.main'
              }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {selectedNode.text || selectedNode.title || 'Node Details'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                  目標：{selectedNode.goal || selectedNode.description || ''}
                </Typography>
                
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1.5 }}>
                  查核點：
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {/* Handle different checkpoint data structures */}
                  {selectedNode.checkpoint_status && selectedNode.checkpoint_status.map((checkpoint, index) => (
                    <Paper key={index} elevation={0} sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      bgcolor: (checkpoint.completed !== undefined ? checkpoint.completed : checkpoint.status === 'completed') 
                        ? 'rgba(76, 175, 80, 0.08)' 
                        : 'rgba(255, 107, 107, 0.08)' 
                    }}>
                      {renderCheckpointStatus(checkpoint)}
                    </Paper>
                  ))}
                  
                  {/* Alternative data structure */}
                  {!selectedNode.checkpoint_status && selectedNode.checkpoints && selectedNode.checkpoints.map((checkpoint, index) => (
                    <Paper key={index} elevation={0} sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      bgcolor: (checkpoint.completed !== undefined ? checkpoint.completed : checkpoint.status === 'completed') 
                        ? 'rgba(76, 175, 80, 0.08)' 
                        : 'rgba(255, 107, 107, 0.08)' 
                    }}>
                      {renderCheckpointStatus(checkpoint)}
                    </Paper>
                  ))}
                  
                  {/* Display message if no checkpoints are found */}
                  {(!selectedNode.checkpoint_status && !selectedNode.checkpoints) && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      無查核點資料
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <ErrorOutlineIcon color="error" />
            <Typography variant="h6" fontWeight="bold">
              常見錯誤提醒
            </Typography>
          </Box>
          
          {analysisData.common_mistakes && analysisData.common_mistakes.length > 0 ? (
            analysisData.common_mistakes.map((mistake, index) => (
              <Paper 
                key={index}
                elevation={1}
                sx={{ 
                  borderRadius: 3,
                  p: 2.5,
                  pb: 1.5,
                  borderLeft: '4px solid',
                  borderColor: mistake.severity === 'high' ? 'error.main' : 'warning.main',
                  bgcolor: mistake.severity === 'high' ? 'rgba(244, 67, 54, 0.05)' : 'rgba(255, 167, 38, 0.05)'
                }}
              >
                <Typography 
                  variant="body2" 
                  color={mistake.severity === 'high' ? 'error.dark' : 'warning.dark'}
                  sx={{ fontWeight: 500, mb: 1 }}
                >
                  {mistake.text}
                </Typography>
                {mistake.original_sentence && (
                  <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary', display: 'block', mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                    觸發語句："{mistake.original_sentence}"
                  </Typography>
                )}
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 2 }}>
              沒有常見錯誤
            </Typography>
          )}
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <LightbulbOutlinedIcon color="warning" />
            <Typography variant="h6" fontWeight="bold">
              改善建議
            </Typography>
          </Box>
          
          {/* Overall suggestion */}
          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
              整體建議
            </Typography>
            <Typography variant="body2" component="div">
              <ol style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {analysisData.suggestions?.overall.split('\n').map((suggestion, index) => (
                  <li key={index} style={{ marginBottom: '8px' }}>{suggestion}</li>
                ))}
              </ol>
            </Typography>
          </Paper>
          
          {/* Stage-specific suggestions */}
          {analysisData.suggestions?.stages && Object.entries(analysisData.suggestions.stages).length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography 
                variant="subtitle1" 
                fontWeight="bold" 
                gutterBottom 
                color="primary"
                sx={{ 
                  mb: 2, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1 
                }}
              >
                <AssessmentIcon fontSize="small" color="primary" />
                各階段建議
              </Typography>
              
              <Box sx={{ 
                borderRadius: 3, 
                overflow: 'hidden', 
                bgcolor: 'rgba(59, 130, 246, 0.03)', 
                p: 2, 
                border: '1px solid rgba(59, 130, 246, 0.1)' 
              }}>
                {Object.entries(analysisData.suggestions.stages).map(([stage, suggestion], index) => (
                  <Accordion 
                    key={index} 
                    defaultExpanded={true}
                    disableGutters
                    sx={{ 
                      mb: index === Object.entries(analysisData.suggestions.stages).length - 1 ? 0 : 1.5, 
                      backgroundColor: 'white', 
                      borderRadius: '12px !important',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(37, 99, 235, 0.08)',
                      '&:before': { display: 'none' },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.12)',
                      }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon color="primary" />}
                      sx={{ 
                        borderRadius: 3,
                        bgcolor: 'rgba(59, 130, 246, 0.04)', 
                        '&.Mui-expanded': {
                          borderBottom: '1px solid rgba(59, 130, 246, 0.1)'
                        }
                      }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="600"
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          color: 'primary.main'
                        }}
                      >
                        <LightbulbOutlinedIcon fontSize="small" color="primary" />
                        {stage}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, py: 2.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          lineHeight: 1.6,
                          color: 'text.primary'
                        }}
                      >
                        {suggestion}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Start: Re-added Block for "2個提問1個舉例" Analysis */}
        {analysisData?.two_questions_one_example_analysis && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <QuestionAnswerIcon color="info" />
              <Typography variant="h6" fontWeight="bold">
                「2個提問1個舉例」分析
              </Typography>
            </Box>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">觀察狀態：</Typography>
                {analysisData.two_questions_one_example_analysis.observed ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="有觀察到"
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    icon={<CancelIcon />}
                    label="未觀察到"
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                建議：
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                {analysisData.two_questions_one_example_analysis.suggestion || '無建議提供。'}
              </Typography>
              {/* Display fulfilling sentences if observed is true and sentences exist */}
              {analysisData.two_questions_one_example_analysis.observed &&
               analysisData.two_questions_one_example_analysis.fulfilling_sentences &&
               analysisData.two_questions_one_example_analysis.fulfilling_sentences.length > 0 && (
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'info.dark' }}>
                    達成模式的語句：
                  </Typography>
                  {analysisData.two_questions_one_example_analysis.fulfilling_sentences.map((sentence, idx) => (
                    <Typography key={idx} variant="caption" display="block" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 0.5 }}>
                      - "{sentence}"
                    </Typography>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        )}
        {/* End: Re-added Block */}

      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', gap: 2, bgcolor: '#f8fafc' }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          sx={{ 
            borderRadius: 2, 
            py: 1, 
            px: 3, 
            boxShadow: '0 4px 10px rgba(37, 99, 235, 0.25)',
            background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
            '&:hover': {
              background: 'linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%)',
            }
          }}
        >
          重新練習
        </Button>
        <Tooltip title={hasAutoSaved ? "資料已自動儲存" : "手動儲存評估報告"}>
          <Button 
            onClick={handleSaveResults}
            variant="outlined"
            startIcon={isLoading ? <CircularProgress size={20} /> : hasAutoSaved ? <DoneIcon /> : <SaveIcon />}
            disabled={isLoading}
            sx={{ 
              borderRadius: 2, 
              py: 1, 
              px: 3,
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#475569',
                bgcolor: 'rgba(100, 116, 139, 0.04)'
              }
            }}
          >
            {isLoading ? '儲存中...' : hasAutoSaved ? '已自動儲存' : '儲存結果'}
          </Button>
        </Tooltip>
        <Button 
          onClick={handleEvaluation} 
          variant="outlined" 
          sx={{ 
            borderRadius: 2, 
            py: 1, 
            px: 3,
            borderColor: '#3b82f6',
            '&:hover': {
              borderColor: '#2563eb',
              bgcolor: 'rgba(37, 99, 235, 0.04)'
            }
          }}
        >
          完成評估
        </Button>
      </DialogActions>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AnalysisDialog; 